import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import CommonDialogDataGrid from "./components/CommonDialogDataGrid";
import UpdateProcessMapDialog from "./mapping/UpdateProcessMapDialog";
import api from "api";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";
import UpdateRuleMapDialog from "./mapping/UpdateRuleMapDialog";
import { Link } from "react-router-dom";

function RuleSet({
  tabValue,
  index,
  processList,
  allRuleSets,
  refreshRuleSets,
  allRules,
}) {
  const [mappedProcessKeys, setMappedProcessKeys] = useState([]);
  const [mappedRuleKeys, setMappedRuleKeys] = useState([]);
  const [dialogProcess, setDialogProcess] = useState(null);
  const [showProcessMappingDialog, setShowProcessMappingDialog] =
    useState(false);
  const [showRuleMappingDialog, setShowRuleMappingDialog] = useState(false);
  const dispatch = useDispatch();

  const handleOpenProcessMappingDialog = (processRow) => {
    setDialogProcess(processRow);
    const selectedProcessKeys = processRow.ruleConfigProcessKeys || [];

    // Ensure they exist in the master process list
    const matchedProcessKeys = processList
      .filter((proc) => selectedProcessKeys.includes(proc.ruleConfigProcessKey))
      .map((proc) => proc.ruleConfigProcessKey);

    setMappedProcessKeys(matchedProcessKeys);

    setShowProcessMappingDialog(true);
  };

  const handleOpenRuleMappingDialog = (processRow) => {
    setDialogProcess(processRow);
    const selectedRuleKeys = processRow.ruleConfigRuleKeys || [];

    const matchedRuleKeys = allRules
      .filter((rule) => selectedRuleKeys.includes(rule.ruleConfigRuleKey))
      .map((rule) => rule.ruleConfigRuleKey);

    setMappedRuleKeys(matchedRuleKeys);

    setShowRuleMappingDialog(true);
  };

  const handleUpdateProcessMap = async (ruleSetKey, addedKeys, removedKeys) => {
    try {
      await api.ruleConfigProcess.updateRuleConfigRuleSetMatch(ruleSetKey, {
        addedKeys: addedKeys,
        removedKeys: removedKeys,
      });
      await refreshRuleSets();
      dispatch(
        setDialog({
          message: "RuleSet to Process mapping updated!",
          title: "Success",
        })
      );
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: err.message })
      );
    } finally {
      setShowProcessMappingDialog(false);
      setDialogProcess(null);
    }
  };

  const handleUpdateRuleMap = async (ruleSetKey, addedKeys, removedKeys) => {
    try {
      await api.ruleConfigProcess.updateRuleConfigRuleMatch(ruleSetKey, {
        addedKeys: addedKeys,
        removedKeys: removedKeys,
      });
      await refreshRuleSets();
      dispatch(
        setDialog({
          message: "RuleSet to Rule mapping updated!",
          title: "Success",
        })
      );
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: err.message })
      );
    } finally {
      setShowRuleMappingDialog(false);
      setDialogProcess(null);
    }
  };

  const columns = [
    { field: "ruleConfigRuleSetKey", headerName: "RuleSet Key", flex: 1 },
    {
      field: "ruleConfigRuleSetName",
      headerName: "RuleSet Name",
      flex: 2,
      renderCell: (params) => (
        <Link
          to={`/rule-builder/edit-ruleset/${params.row.ruleConfigRuleSetKey}`}
        >
          {params.value}
        </Link>
      ),
    },
    { field: "priority", headerName: "Priority", flex: 1 },
    { field: "ruleCount", headerName: "# of Rules", flex: 1 },
    {
      field: "mapProcesses",
      headerName: "Map Processes",
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => handleOpenProcessMappingDialog(params.row)}
        >
          Map Processes
        </Button>
      ),
    },
    {
      field: "mapRules",
      headerName: "Map Rules",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => handleOpenRuleMappingDialog(params.row)}
        >
          Map Rules
        </Button>
      ),
    },
  ];

  const renderProcessMapDialog = () => {
    if (!dialogProcess || !showProcessMappingDialog) return null;
    return (
      <UpdateProcessMapDialog
        open={showProcessMappingDialog}
        onClose={() => {
          setShowProcessMappingDialog(false);
          setDialogProcess(null);
        }}
        onSave={(addedKeys, removedKeys) =>
          handleUpdateProcessMap(
            dialogProcess.ruleConfigRuleSetKey,
            addedKeys,
            removedKeys
          )
        }
        allProcessList={processList}
        mappedProcessKeys={mappedProcessKeys}
      />
    );
  };

  const renderRuleMapDialog = () => {
    if (!dialogProcess || !showRuleMappingDialog) return null;
    return (
      <UpdateRuleMapDialog
        open={showRuleMappingDialog}
        onClose={() => {
          setShowRuleMappingDialog(false);
          setDialogProcess(null);
        }}
        onSave={(addedKeys, removedKeys) =>
          handleUpdateRuleMap(
            dialogProcess.ruleConfigRuleSetKey,
            addedKeys,
            removedKeys
          )
        }
        allRules={allRules}
        mappedRuleKeys={mappedRuleKeys}
      />
    );
  };

  return (
    <div hidden={tabValue !== index}>
      {renderProcessMapDialog()}
      {renderRuleMapDialog()}
      <Box
        sx={{
          height: "calc(100vh - 175px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CommonDialogDataGrid
          rows={allRuleSets}
          getRowId={(row) => row.ruleConfigRuleSetKey}
          columns={columns}
          checkboxSelection={false}
          showSearch={true}
          title="Available Rule Sets"
        />
      </Box>
    </div>
  );
}

export default RuleSet;
